import { Injectable, Injector, inject } from '@angular/core';
import { Configuration, FeatureFlag } from '../../app/models/configuration.models';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { from, firstValueFrom, throwError } from 'rxjs';
import { environment } from '../../environments/environment';
import { map, catchError } from 'rxjs/operators';
import { LoggerService } from 'src/services/logger-services/logger.service';
import { TokenService } from '../../shared/services/token/token.service';

@Injectable({
  providedIn: 'root'
})
export class ConfigurationService {
  public configData: any | undefined;
  private featureFlagData: any | undefined;
  private baseUrl: string = environment.baseUrl;
  private hasAuthError: boolean = false;
  private readonly tenantId: string = "";
  private loggerService: LoggerService | null = null;
  private readonly tokenService = inject(TokenService);

  /** In-memory UI release version for i18n (V2 vs V3). Not set for v1.x (no i18n). */
  private releaseVersion: '2.0' | '3.0' | null = null;

  private readonly ConfigurationError = (error:any) =>
    `[ConfigurationService] - ${JSON.stringify(error)}`

  constructor(
    private readonly http: HttpClient,
    private readonly injector: Injector){
    this.tenantId = environment.TenantId;
    }

  private get logger(): LoggerService {
    if(!this.loggerService){
      this.loggerService = this.injector.get(LoggerService);
    }
    return this.loggerService;
  }

async loadConfiguration(): Promise<Configuration> {
  try {
    // Add cache-busting timestamp to prevent browser caching
    const cacheBuster = new Date().getTime();
    const response = await firstValueFrom(
      from(
        fetch(`config/config.json?v=${cacheBuster}`, {
          cache: 'reload', // Bypass cache entirely, always fetch fresh
          headers: {
            'Cache-Control': 'no-cache, no-store, must-revalidate',
            'Pragma': 'no-cache',
            'Expires': '0'
          }
        }).then(response => response.json())
      )
    );
    this.configData = response;
    this.baseUrl = this.configData.apiBaseUrl || this.baseUrl;
    return this.configData;
  } catch (err:any) {
    this.logger.logError(this.ConfigurationError(err),"loadConfiguration");
    return Promise.reject(err);
  }
}

get config(): Configuration | undefined {    
    return this.configData;
}

setReleaseVersion(version: '2.0' | '3.0'): void {
  this.releaseVersion = version;
}

getReleaseVersion(): '2.0' | '3.0' | null {
  return this.releaseVersion;
}

async loadFeatureFlags(): Promise<FeatureFlag> {
  try {
    this.hasAuthError = false;


    const response = await firstValueFrom(
      this.getFeatureConfig(this.configData.FEATURE_CONFIG_PROFILE_ID).pipe(
        map(httpResponse => httpResponse.body),
        catchError(err => {
          this.logger.logError(this.ConfigurationError(err),"loadFeatureFlags");
          if (err.status === 401 || err.status === 403) {
            this.hasAuthError = true;
            this.logger.logError(this.ConfigurationError(err),"loadFeatureFlags");
          }
          return throwError(() => err);
        })
      )
    );
    this.featureFlagData = response;
    console.log('[ConfigurationService] Feature flags loaded successfully:', this.featureFlagData);
    return this.featureFlagData;
  } catch (err: any) {
    this.logger.logError(this.ConfigurationError(err),"loadFeatureFlags");
    this.featureFlagData = {};
    console.log('[ConfigurationService] Feature flags not loaded:', this.featureFlagData);
    return this.featureFlagData;
  }
}

public getFeatureConfig(configProfileId: string) {
  try{
  const headers = new HttpHeaders({
    'Authorization': `Bearer ${this.tokenService.getAccessToken()}`,
    'Content-Type': 'application/json'
  });

  const basePayload = {
    application_name: this.configData.APPLICATION_NAME,
    environment: this.configData.APP_CONFIG_ENVIRONMENT
  };

  // Fetch the api response
  return this.http.post<any>(
    `${this.baseUrl}/v2/api/app-config`,
    { ...basePayload, config_profile_id: configProfileId },
    {headers: headers, observe: 'response' }
  );
} catch (err: any) {
  this.logger.logError(this.ConfigurationError(err),"getFeatureConfig");
  return throwError(() => err);
}
}

get featureFlag(): FeatureFlag | undefined {
  return this.featureFlagData;
}

/**
 * Check if there was an authentication/authorization error when loading feature flags
 * @returns true if there was an auth error (401/403), false otherwise
 */
hasFeatureConfigAuthError(): boolean {
  return this.hasAuthError;
}

async isNewChatbotEnabled(): Promise<boolean> {
  try{
    // Always return true in development environment
    if (this.configData?.ENVIRONMENT === 'dev' || this.configData?.ENVIRONMENT === 'development') {
      return true;
    }

    if (!this.featureFlagData) {
      await this.loadFeatureFlags();
    }

    // Check if response contains 'v2.0' and it's 'true'
    if (this.featureFlagData && this.featureFlagData['configuration']?.[this.tenantId]?.['v2.0'] === 'true') {
      return true;
    }
    return false; // Domain Management is not enabled

  } catch (error:any) {
    this.logger.logError(this.ConfigurationError(error),"isNewChatbotEnabled");
    return false;
  }
}

async isNewsAndFeedsEnabled(): Promise<boolean> {
  try{
    if (!this.featureFlagData) {
      await this.loadFeatureFlags();
    }

    // Check if response contains 'news_and_feeds' and it's 'true'
    if (this.featureFlagData && this.featureFlagData['news_and_feeds'] === 'true') {
      return true;
    }

    return false; // Default to old UI

  } catch (error:any) {
    this.logger.logError(this.ConfigurationError(error),"isNewsAndFeedsEnabled");
    return false;
  } 
}

async isDomainManagementEnabled(): Promise<boolean> {
  try{
    if (!this.featureFlagData) {
      await this.loadFeatureFlags();
    }

    // Check if response contains 'news_and_feeds' and it's 'true'
    if (this.featureFlagData && this.featureFlagData['configuration']?.[this.tenantId]?.['domain_management']) {
      return true;
    }

    return false; // set to false

  } catch (error:any) {
    this.logger.logError(this.ConfigurationError(error),"isDomainManagementEnabled");
    return false;
  } 
}

/**
 * feature flag to check if file upload is enabled
 * @returns true if file upload is enabled, false otherwise
 */
async isFileUploadEnabled(): Promise<boolean> {
  try{
    if (!this.featureFlagData) {
      await this.loadFeatureFlags();
    }
    // Check if response contains 'file_upload' and it's 'true'
    if (this.featureFlagData && this.featureFlagData['configuration']?.[this.tenantId]?.['file_upload']) {
      return true;
    }
    return false; // set to false
  } catch (error:any) {
    this.logger.logError(this.ConfigurationError(error),"isFileUploadEnabled");
    return false; // set to false
  }
}

/**
 * Feature flag to check if retrieval mode toggle is enabled for regulations streaming.
 * @returns true when app-config has retrieval_mode enabled for the tenant
 */
async isRetrievalModeEnabled(): Promise<boolean> {
  try {
    if (!this.featureFlagData) {
      await this.loadFeatureFlags();
    }
    const tenantConfig = this.featureFlagData?.['configuration']?.[this.tenantId];
    if (!tenantConfig) {
      return false;
    }
    return this.isTruthyFeatureFlag(tenantConfig['retrieval_mode']);
  } catch (error: any) {
    this.logger.logError(this.ConfigurationError(error), 'isRetrievalModeEnabled');
    return false;
  }
}

/** Reads boolean feature flags that may be true or the string 'true'. */
private isTruthyFeatureFlag(value: unknown): boolean {
  return value === true || value === 'true';
}

/**
 * Get API base URL for the modern API service
 */
getApiBaseUrl(): string {
  return this.configData?.apiBaseUrl || this.baseUrl || 'https://devapi-chatbot.freyrapps.com';
}

/**
 * Get URF API base URL
 */
getUrfApiBaseUrl(): string {
  return this.configData?.URF_API || 'https://devurf-api.freyrapps.com';
}

/**
 * Get environment name
 */
getEnvironment(): string {
  return this.configData?.ENVIRONMENT || 'dev' || 'development';
}

/**
 * Check if debug mode is enabled
 */
isDebugMode(): boolean {
  return this.configData?.debugMode || false;
}

/**
 * Check if a specific agent is enabled via feature flags
 * @param agentId - The ID of the agent (e.g., 'via_agent')
 * @returns true if the agent is enabled in feature flags, false otherwise
 */
async isAgentEnabled(agentId: string): Promise<boolean> {
  try {
    if (!this.featureFlagData) {
      await this.loadFeatureFlags();
    }

    // Check if response contains enrolled agents configuration for this tenant
    // Structure: configuration[tenantId].enrolled_agents[agentId]
    const enrolledAgents = this.featureFlagData?.['configuration']?.[this.tenantId]?.['enrolled_agents'];
    console.log('[ConfigurationService] Enrolled agents config:', enrolledAgents);
    if (enrolledAgents && typeof enrolledAgents === 'object') {
      const isEnabled = enrolledAgents[agentId];
      console.log(`[ConfigurationService] Agent '${agentId}' enrolled status:`, isEnabled);
      return isEnabled === true || isEnabled === 'true';
    }

    // If enrolled agents config doesn't exist, default to false
    console.log(`[ConfigurationService] Agent '${agentId}' not found in enrolled agents, defaulting to false`);
    return false;

  } catch (error: any) {
    this.logger.logError(this.ConfigurationError(error), "isAgentEnabled");
    return false;
  }
}

// ============================================
// NEW METHODS FOR V3.0 AGENTIC UI
// ============================================

/**
 * Check if Agentic UI is enabled for the current tenant
 * @returns true if agentic_ui_enabled is true, false otherwise
 */
async isAgenticUIEnabled(): Promise<boolean> {
  try {
    if (!this.featureFlagData) {
      await this.loadFeatureFlags();
    }

    // Check if agentic_ui_enabled exists and is true
    const agenticFlag = this.featureFlagData?.['configuration']?.[this.tenantId]?.['agentic_ui_enabled'];
    
    console.log('[ConfigurationService] agentic_ui_enabled flag:', agenticFlag);
    return agenticFlag === true;

  } catch (error: any) {
    this.logger.logError(this.ConfigurationError(error), "isAgenticUIEnabled");
    return false; // Default to false on error
  }
}

/**
 * Tenant feature gate for AI Workbench (iframe). Requires v2.0 chatbot, agentic UI, and
 * `workbench_access` enabled in feature flags. Intersect with WorkbenchAccessService.hasWorkbenchAccess (privilege).
 */
async isWorkbenchAccessEnabled(): Promise<boolean> {
  try {
    if (!this.featureFlagData) {
      await this.loadFeatureFlags();
    }
    const configEnv = this.configData?.ENVIRONMENT as string | undefined;
    if (configEnv === 'dev' || configEnv === 'development') {
      this.logger.logInfo(
        '[ConfigurationService] workbench_access tenant gate skipped (ENVIRONMENT=dev); privilege enforced in wrapper',
        'isWorkbenchAccessEnabled'
      );
      return true;
    }

    const cfg = this.featureFlagData?.['configuration']?.[this.tenantId];
    if (!cfg || typeof cfg !== 'object') {
      return false;
    }

    const v2Enabled = cfg['v2.0'] === 'true';
    const agenticEnabled = cfg['agentic_ui_enabled'] === true;
    const workbenchFlag = cfg['workbench_access'] === true || cfg['workbench_access'] === 'true';

    this.logger.logInfo(`[ConfigurationService] workbench_access gate: v2Enabled=${v2Enabled}, agenticEnabled=${agenticEnabled}, workbenchFlag=${workbenchFlag}`, "isWorkbenchAccessEnabled");
    return v2Enabled && agenticEnabled && workbenchFlag;
  } catch (error: any) {
    this.logger.logError(this.ConfigurationError(error), "isWorkbenchAccessEnabled");
    return false; // Default to false on error
  }
}

/**
 * Get enrolled agents for the current tenant
 * @returns Object with agent IDs as keys and boolean values, or empty object if not available
 */
getEnrolledAgents(): { [key: string]: boolean } {
  try {
    if (!this.featureFlagData) {
      console.log('[ConfigurationService] Feature flags not loaded yet');
      return {};
    }

    const enrolledAgents = this.featureFlagData?.['configuration']?.[this.tenantId]?.['enrolled_agents'];
    
    if (enrolledAgents && typeof enrolledAgents === 'object') {
      console.log('[ConfigurationService] Enrolled agents:', enrolledAgents);
      return enrolledAgents;
    }

    console.log('[ConfigurationService] No enrolled agents found');
    return {};

  } catch (error: any) {
    this.logger.logError(this.ConfigurationError(error), "getEnrolledAgents");
    return {};
  }
}

/**
 * Get list of enrolled agent IDs where value is true
 * @returns Array of agent IDs that are enrolled (value = true)
 */
getEnrolledAgentIds(): string[] {
  try {
    const enrolledAgents = this.getEnrolledAgents();
    
    // Filter to get only agents where value is true
    const agentIds = Object.keys(enrolledAgents).filter(
      agentId => enrolledAgents[agentId] === true
    );
    
    console.log('[ConfigurationService] Enrolled agent IDs (true only):', agentIds);
    return agentIds;

  } catch (error: any) {
    this.logger.logError(this.ConfigurationError(error), "getEnrolledAgentIds");
    return [];
  }
}

/**
 * Check if agents should be enabled in v2.0 chatbot
 * Reads from config.json enableAgentsInV2 flag
 * @returns boolean - true if agents should be shown in v2.0, false otherwise
 */
isAgentsEnabledInV2(): boolean {
  try {
    const enableAgents = this.configData?.['enableAgentsInV2'];
    console.log('[ConfigurationService] enableAgentsInV2 flag:', enableAgents);
    return enableAgents === true;
  } catch (error: any) {
    this.logger.logError(this.ConfigurationError(error), "isAgentsEnabledInV2");
    return false; // Default to false on error
  }
}
}
